import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ConfirmPasswordCurrentUserPayload } from '@/features/auth/types/login';
import { HttpClient, buildEndpointUrl } from '@/libs/axios';
import { MutateDefaultOptions } from '@/models/api';
import { getAxiosBackendError } from '@/utils/errors';

export const getConfirmPasswordCurrentUserMutateKey = () => ['confirmPasswordCurrentUser'];

export const getConfirmPasswordCurrentUserEndpoint = () =>
    buildEndpointUrl('USERS', { additionalPaths: ['register', 'confirm'] });

export const apiConfirmPasswordCurrentUser = (payload: ConfirmPasswordCurrentUserPayload) =>
    HttpClient.patch<void>(getConfirmPasswordCurrentUserEndpoint(), payload);

//

export const useConfirmPasswordCurrentUser = () => {
    const { mutateAsync, isPending: isConfirmingPassword } = useMutation<
        unknown,
        AxiosError,
        ConfirmPasswordCurrentUserPayload
    >({
        mutationKey: getConfirmPasswordCurrentUserMutateKey(),
        mutationFn: (payload) => apiConfirmPasswordCurrentUser(payload),
    });

    const confirmPasswordCurrentUser = useCallback(
        async (
            payload: ConfirmPasswordCurrentUserPayload,
            { onSuccess, onError, onSettled }: MutateDefaultOptions = {},
        ) => {
            try {
                await mutateAsync(payload);
            } catch (error: unknown) {
                await onError?.(getAxiosBackendError(error, true));
                await onSettled?.();
                return;
            }

            await onSuccess?.();
            await onSettled?.();
        },
        [mutateAsync],
    );

    return { confirmPasswordCurrentUser, isConfirmingPassword };
};
